import classNames from 'classnames';
import { RenderFormNodeComponent } from 'components/common-form/builder/form/FormBuilder';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { GreeksFeesMetricsTabContent } from 'components/common-form/sidePanel/info/QuoteInfos/GreeksFeesMetricsTabContent';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { GraphModalFieldMetadata } from 'models/forms/fields/GraphModalFieldMetadata';
import { FC, MouseEventHandler, useState } from 'react';
import { Modal, ModalBody, ModalHeader, ModalTitle } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';

export const GraphModalFieldLabel = styled.div`
  cursor: pointer;

  &:hover {
    background-color: var(--bs-bg-lvl2);
  }
`;

export const GraphModal = styled(Modal)`
  --bs-modal-width: 80vw;
  @media (min-width: 2000px) {
    --bs-modal-width: 2000px;
  }
`;

export const GraphModalBody = styled(ModalBody)`
  padding-bottom: 0;
`;

export const GraphModalField: FC<ProductNodeBuilderProps> = ({
  tabId,
  node,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  level,
  mustForceAdvancedMode,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { modalFieldId } = useFieldMetadata<GraphModalFieldMetadata>(node.id)!;

  const toggle = () => setIsModalOpen(!isModalOpen);

  const stopModalClickBubbling: MouseEventHandler<HTMLElement> = event => event.stopPropagation();
  const hasChildren = (node.children?.length ?? 0) > 0;

  return (
    <>
      <BlockOrientationFormGroup inputId={node.id}>
        <GraphModalFieldLabel
          className="d-flex flex-row align-items-center justify-content-between h-100"
          onClick={toggle}
        >
          <div className="px-2 user-select-none">
            <i className="icon icon-sm">insights</i>
          </div>
          <div className="ms-auto">
            <button className={classNames('btn btn-icon', 'btn-flat')} onClick={toggle}>
              <i className="icon icon-sm">visibility</i>
            </button>
          </div>
        </GraphModalFieldLabel>
      </BlockOrientationFormGroup>
      <GraphModal data-e2e="modal" show={isModalOpen} keyboard={false} scrollable={false} onHide={toggle}>
        <ModalHeader className="pb-2" closeButton>
          <ModalTitle as="h3">
            <span className="d-flex align-items-center">
              <FormattedMessage id={`form.${modalFieldId}.title` as any} />
            </span>
          </ModalTitle>
        </ModalHeader>
        <GraphModalBody onClick={stopModalClickBubbling}>
          <div className="row">
            {hasChildren && (
              <div className="col-lg-9">
                {node.children?.map(c => (
                  <RenderFormNodeComponent
                    key={c.id}
                    tabId={tabId}
                    level={level + 1}
                    workspaceType="newBatch"
                    quoteWorkspaceVersion={quoteWorkspaceVersion}
                    node={c}
                    mustForceAdvancedMode={mustForceAdvancedMode}
                    batchWorkspaceVersion={batchWorkspaceVersion}
                  />
                ))}
              </div>
            )}
            <div className={`col-lg-${hasChildren ? 3 : 12} d-flex flex-column overflow-y-auto max-height-75vh}`}>
              <GreeksFeesMetricsTabContent tabId={tabId} workspaceType={'newBatch'} />
            </div>
          </div>
        </GraphModalBody>
      </GraphModal>
    </>
  );
};

GraphModalField.displayName = 'GraphModalField';
